/**
 * How one person reads one table: which columns, in which order, and how many
 * rows a page. The request of 7 October 2026, section 3.
 *
 * Kept on their account, a table at a time, so it is the same on every device
 * until they change it. A layout is only ever a list of keys: what a column
 * shows is the screen's business, and a layout written when a table had other
 * columns is read against the columns it has now.
 */
export interface TableLayout {
  /** The columns in the reader's order. Empty for the table's own order. */
  order: string[];
  /** The columns the reader left out. */
  hidden: string[];
  /** The rows a page, or null for the table's own. */
  pageSize: number | null;
}

export const NO_LAYOUT: TableLayout = { order: [], hidden: [], pageSize: null };

/**
 * The most rows any list answers with in a page. The tables offer 25, 50, 100
 * and 200, and the archive and the activity log start at 80, so any whole
 * number up to the cap is a size some table may show. The API's
 * `TableLayouts.MaxPageSize`.
 */
export const TABLE_PAGE_SIZE_MAX = 200;

/** What the API refuses a layout with, word for word. */
export const TABLE_LAYOUT_REFUSAL = {
  table: 'That is not a table.',
  columns: "That is not a list of this table's columns.",
  pageSize: 'Rows a page is a whole number from 1 to 200.',
} as const;

export const TABLE_KEY_MAX = 60;
export const TABLE_COLUMNS_MAX = 60;

/** A table's key, or a column's: letters, digits and the few marks the screens use in theirs. */
export function isTableKey(key: string): boolean {
  return key.length > 0 && key.length <= TABLE_KEY_MAX && /^[A-Za-z0-9:._-]+$/.test(key);
}

export function isTablePageSize(size: number | null): boolean {
  return size === null || (Number.isInteger(size) && size >= 1 && size <= TABLE_PAGE_SIZE_MAX);
}

function isColumnList(keys: readonly string[]): boolean {
  return (
    keys.length <= TABLE_COLUMNS_MAX &&
    keys.every(isTableKey) &&
    new Set(keys).size === keys.length
  );
}

/** The field a layout is refused on and why, or null for one that may be kept. */
export function tableLayoutRefusal(
  table: string,
  layout: TableLayout,
): { field: 'table' | 'order' | 'hidden' | 'pageSize'; message: string } | null {
  if (!isTableKey(table)) return { field: 'table', message: TABLE_LAYOUT_REFUSAL.table };
  if (!isColumnList(layout.order)) return { field: 'order', message: TABLE_LAYOUT_REFUSAL.columns };
  if (!isColumnList(layout.hidden)) return { field: 'hidden', message: TABLE_LAYOUT_REFUSAL.columns };
  return isTablePageSize(layout.pageSize)
    ? null
    : { field: 'pageSize', message: TABLE_LAYOUT_REFUSAL.pageSize };
}

/** One column as the layout sees it: its key, and whether the table can do without it. */
export interface LayoutColumn {
  key: string;
  /** An essential column, the job order number among them: always shown. */
  locked?: boolean;
  /**
   * A column the table can show and does not until its reader asks for it: left
   * out for anybody who never chose, and for a layout kept before the column
   * existed. The request of 8 October 2026 (second).
   */
  optional?: boolean;
}

/** One row of the Customize columns list. */
export interface ColumnChoice {
  key: string;
  shown: boolean;
  locked: boolean;
}

/**
 * The table's columns in the reader's order, each with whether it is shown.
 *
 * Read against the columns the table has NOW. A key the layout names that the
 * table no longer has is dropped; a column the layout has never heard of keeps
 * the place the table gives it, after the column it follows there, so a column
 * added in a release appears where it was designed to. A locked column is
 * shown whatever the layout says.
 *
 * A column the layout has a place for is shown unless the layout hides it. One
 * it has never heard of stands as the table gives it: shown, unless it is one
 * the table only offers (`optional`), which waits to be asked for.
 */
export function columnChoices(
  columns: readonly LayoutColumn[],
  layout: TableLayout | null | undefined,
): ColumnChoice[] {
  const known = new Map(columns.map((column) => [column.key, column]));
  const hidden = new Set(layout?.hidden ?? []);
  // Every column the list held when the reader kept it, shown or not.
  const named = new Set(layout?.order ?? []);

  const placed = (layout?.order ?? []).filter((key) => known.has(key));
  const seen = new Set(placed);
  columns.forEach((column, index) => {
    if (seen.has(column.key)) return;
    // After the nearest column before it that already has a place; first if none has.
    let at = 0;
    for (let before = index - 1; before >= 0; before -= 1) {
      const found = placed.indexOf(columns[before]!.key);
      if (found >= 0) {
        at = found + 1;
        break;
      }
    }
    placed.splice(at, 0, column.key);
    seen.add(column.key);
  });

  return placed.map((key) => {
    const column = known.get(key)!;
    const locked = column.locked === true;
    const shown = !hidden.has(key) && (named.has(key) || column.optional !== true);
    return { key, shown: locked || shown, locked };
  });
}

/** The keys of the columns to draw, in order. */
export function shownColumns(
  columns: readonly LayoutColumn[],
  layout: TableLayout | null | undefined,
): string[] {
  return columnChoices(columns, layout)
    .filter((choice) => choice.shown)
    .map((choice) => choice.key);
}

/** A column moved from one place in the list to another. Out of range leaves the list as it is. */
export function moveColumn(choices: readonly ColumnChoice[], from: number, to: number): ColumnChoice[] {
  if (from === to || from < 0 || to < 0 || from >= choices.length || to >= choices.length) {
    return [...choices];
  }
  const next = [...choices];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/** A column shown or left out. A locked one stays shown. */
export function toggleColumn(choices: readonly ColumnChoice[], key: string): ColumnChoice[] {
  return choices.map((choice) =>
    choice.key === key && !choice.locked ? { ...choice, shown: !choice.shown } : choice,
  );
}

/** The list as a layout to keep, with the rows a page beside it. */
export function layoutOf(choices: readonly ColumnChoice[], pageSize: number | null): TableLayout {
  return {
    order: choices.map((choice) => choice.key),
    hidden: choices.filter((choice) => !choice.shown).map((choice) => choice.key),
    pageSize,
  };
}

/**
 * Whether a layout changes anything about the table as it comes: the order,
 * what is shown, or the rows a page. What Reset is offered against.
 */
export function isCustomLayout(
  columns: readonly LayoutColumn[],
  layout: TableLayout | null | undefined,
): boolean {
  if (layout === null || layout === undefined) return false;
  if (layout.pageSize !== null) return true;
  const choices = columnChoices(columns, layout);
  // Against the table as it comes: an offered column left out is nothing changed, and one shown is.
  const asItComes = columnChoices(columns, null);
  return choices.some(
    (choice, index) =>
      choice.key !== asItComes[index]!.key || choice.shown !== asItComes[index]!.shown,
  );
}
